import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { LuMail } from 'react-icons/lu';
import Navbar from './Navbar';
import ContactForm, { NEED_OPTIONS } from './ContactForm';
import { CadHeroScene, CadSite, CadGestionale } from './CadDrawings';
import { CONTACT_EMAIL } from '../data/site';
import '../App.css';
import '../siti-landing.css';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const SitiLanding = () => {
  useEffect(() => {
    document.title = 'Il sito te lo facciamo noi. Il gestionale anche. | Tre Lune';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Studiamo insieme la tua struttura, poi ti diamo la soluzione. Sito custom, gestionale proprietario, o entrambi.'
      );
    }
    document.body.classList.add('siti-cad');
    const theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute('content');
    if (theme) theme.setAttribute('content', '#F6F6F4');
    return () => {
      document.body.classList.remove('siti-cad');
      if (theme && prevTheme) theme.setAttribute('content', prevTheme);
    };
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contatti')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="siti-page">
      <div className="cad-grid" aria-hidden="true" />
      <div className="cad-vignette" aria-hidden="true" />

      <Navbar />

      <section className="cad-hero" id="offerta">
        <div className="cad-hero-copy">
          <motion.p
            className="cad-kicker"
            initial="hidden"
            animate="show"
            variants={fadeUp}
          >
            01 · SULLA TUA STRUTTURA
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.06 }}
          >
            IL SITO TE LO FACCIAMO NOI.
            <br />
            IL GESTIONALE ANCHE.
          </motion.h1>
          <motion.p
            className="cad-lead"
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.12 }}
          >
            Studiamo insieme la tua struttura, poi ti diamo la soluzione.
          </motion.p>
          <motion.div
            className="cad-hero-offer"
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.18 }}
          >
            <p>
              Il fronte che i clienti vedono e il motore che usi tu si fanno sulla
              tua operatività: uno, l’altro, o il sistema unico. Niente tema, niente
              software già chiuso.
            </p>
          </motion.div>
          <motion.a
            href="#contatti"
            className="btn btn-primary"
            onClick={scrollToContact}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ delay: 0.24 }}
          >
            Avvia il progetto
          </motion.a>
        </div>
        <div className="cad-hero-art">
          <CadHeroScene />
        </div>
      </section>

      <section className="cad-block" id="sito">
        <div className="cad-block-art">
          <CadSite />
        </div>
        <motion.div
          className="cad-block-copy"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <p className="cad-kicker">02 · IL SITO</p>
          <h2>Davvero personalizzato. Non il Solito Template.</h2>
          <p>
            I template si riconoscono in tre secondi. Il tuo sito no: architettura,
            testi, flussi e codice fatti sulla tua attività — non su un tema da
            marketplace con lo stesso slider del concorrente.
          </p>
          <div className="cad-split">
            <article>
              <span>Template</span>
              <p>Uguale a cento altri. Limiti del tema. Plugin che si rompono.</p>
            </article>
            <article>
              <span>Custom</span>
              <p>Tuo. Veloce. Fatto per farsi chiamare. Codice e dominio a tuo nome.</p>
            </article>
          </div>
        </motion.div>
      </section>

      <section className="cad-block cad-block-flip" id="gestionale">
        <motion.div
          className="cad-block-copy"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <p className="cad-kicker">03 · IL GESTIONALE</p>
          <h2>
            Il tuo CRM proprietario.
            <br />
            Basta pagare per ogni utente.
          </h2>
          <p>
            I SaaS ti fanno pagare ogni utente e ti chiudono le funzioni. Il
            gestionale te lo costruiamo noi, sulla tua struttura: niente canone
            per postazione, niente tetto alla personalizzazione. I dati restano tuoi.
          </p>
          <div className="cad-split">
            <article>
              <span>SaaS</span>
              <p>Costo a slot. Moduli bloccati. Adatti tu al software.</p>
            </article>
            <article>
              <span>Su misura</span>
              <p>Cucito sulla tua struttura. Zero costo a utente. Nessun modulo chiuso.</p>
            </article>
          </div>
        </motion.div>
        <div className="cad-block-art">
          <CadGestionale />
        </div>
      </section>

      <ContactForm
        needOptions={NEED_OPTIONS}
        source="siti"
      />

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <span className="text-gradient footer-brand">Tre Lune Technologies</span>
              <div className="footer-contacts">
                <a href={`mailto:${CONTACT_EMAIL}`}>
                  <LuMail aria-hidden="true" /> {CONTACT_EMAIL}
                </a>
              </div>
            </div>
            <nav className="footer-links" aria-label="Piè di pagina">
              <a href="/#servizi">Servizi</a>
              <a href="#contatti">Contatti</a>
              <a href="/siti">Siti e gestione</a>
              <a href="/privacy-policy">Privacy</a>
            </nav>
            <p className="footer-copy">© 2026 Tre Lune Technologies. P.IVA 08740380723</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SitiLanding;
